import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Globe2, LayoutDashboard, Boxes, Wallet, Calculator as CalcIcon, Cpu,
  ShieldAlert, MessageSquare, Settings as SettingsIcon, CreditCard, Bell, LogOut, Menu, X, Factory,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/AppContext";
import { ThemeToggle, LanguageMenu, CurrencyMenu } from "@/components/shared/Controls";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

import Overview from "@/components/dashboard/Overview";
import Opportunities from "@/components/dashboard/Opportunities";
import Capital from "@/components/dashboard/Capital";
import Calculator from "@/components/dashboard/Calculator";
import Agents from "@/components/dashboard/Agents";
import RiskPanel from "@/components/dashboard/RiskPanel";
import ChatPanel from "@/components/dashboard/ChatPanel";
import SettingsPanel from "@/components/dashboard/SettingsPanel";
import Plans from "@/components/dashboard/Plans";
import Suppliers from "@/components/dashboard/Suppliers";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [alerts, setAlerts] = useState({ alerts: [], unread: 0 });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const TABS = [
    { id: "overview", label: t("tab_overview"), icon: LayoutDashboard },
    { id: "opportunities", label: t("tab_opportunities"), icon: Boxes },
    { id: "suppliers", label: "Fornecedores", icon: Factory },
    { id: "capital", label: t("tab_capital"), icon: Wallet },
    { id: "calculator", label: t("tab_calculator"), icon: CalcIcon },
    { id: "agents", label: t("tab_agents"), icon: Cpu },
    { id: "risk", label: t("tab_risk"), icon: ShieldAlert },
    { id: "chat", label: t("tab_chat"), icon: MessageSquare },
    { id: "settings", label: t("tab_settings"), icon: SettingsIcon },
    { id: "plans", label: t("tab_plans"), icon: CreditCard },
  ];

  const loadAlerts = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts");
      setAlerts(data);
    } catch {}
  }, []);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const markAllRead = async () => {
    await api.post("/alerts/read-all");
    loadAlerts();
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const renderTab = () => {
    switch (tab) {
      case "overview": return <Overview onNavigate={setTab} refreshAlerts={loadAlerts} />;
      case "opportunities": return <Opportunities refreshAlerts={loadAlerts} />;
      case "suppliers": return <Suppliers />;
      case "capital": return <Capital />;
      case "calculator": return <Calculator />;
      case "agents": return <Agents />;
      case "risk": return <RiskPanel />;
      case "chat": return <ChatPanel />;
      case "settings": return <SettingsPanel />;
      case "plans": return <Plans user={user} />;
      default: return null;
    }
  };

  const NavItems = ({ onClick, idPrefix = "" }) => (
    <nav className="space-y-1">
      {TABS.map((tItem) => {
        const Active = tab === tItem.id;
        return (
          <button
            key={tItem.id}
            onClick={() => { setTab(tItem.id); onClick?.(); }}
            data-testid={`${idPrefix}tab-${tItem.id}`}
            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              Active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`}
          >
            <tItem.icon className="h-4 w-4" /> {tItem.label}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-border p-4 sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-2 mb-6">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
            <Globe2 className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-heading font-bold tracking-tight">GLOBAL PROFIT AI</span>
        </div>
        <ScrollArea className="flex-1"><NavItems /></ScrollArea>
        <div className="pt-4 border-t border-border mt-4">
          <div className="px-2 mb-2">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.plan} · {user?.email}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-muted-foreground" onClick={doLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> {t("logout")}
          </Button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-72 bg-card border-r border-border p-4 flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <span className="font-heading font-bold">GLOBAL PROFIT AI</span>
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}><X className="h-4 w-4" /></Button>
            </div>
            <NavItems onClick={() => setSidebarOpen(false)} idPrefix="m-" />
            <div className="mt-auto pt-4 border-t border-border space-y-2">
              <div className="flex items-center justify-between px-1">
                <span className="text-xs text-muted-foreground">Moeda de exibição</span>
                <CurrencyMenu />
              </div>
              <Button variant="ghost" className="w-full justify-start text-muted-foreground" onClick={doLogout}>
                <LogOut className="h-4 w-4 mr-2" /> {t("logout")}
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
          <div className="h-16 px-4 sm:px-6 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu"><Menu className="h-5 w-5" /></Button>
              <h1 className="font-heading text-lg font-semibold truncate">{TABS.find((x) => x.id === tab)?.label}</h1>
            </div>
            <div className="flex items-center gap-0.5 sm:gap-1">
              <span className="hidden sm:inline-flex"><CurrencyMenu /></span>
              <LanguageMenu />
              <ThemeToggle />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="alerts-bell">
                    <Bell className="h-4 w-4" />
                    {alerts.unread > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center">{alerts.unread}</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-0">
                  <div className="flex items-center justify-between p-3 border-b border-border">
                    <span className="font-semibold text-sm">Alertas</span>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllRead} data-testid="mark-all-read">Marcar lidos</Button>
                  </div>
                  <ScrollArea className="max-h-80">
                    {alerts.alerts.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground">Nenhum alerta ainda.</p>
                    ) : alerts.alerts.map((a) => (
                      <div key={a.alert_id} className={`p-3 border-b border-border text-sm ${!a.read ? "bg-secondary/40" : ""}`}>
                        <p>{a.message}</p>
                        <p className="text-[11px] text-muted-foreground mt-1 font-mono">{new Date(a.created_at).toLocaleString("pt-BR")}</p>
                      </div>
                    ))}
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto">
          {renderTab()}
        </main>
      </div>
    </div>
  );
}
